import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { useContent } from "@/i18n/content";
import { useI18n } from "@/i18n";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] } },
};

export default function Services() {
  const [active, setActive] = useState(0);
  const { SERVICES } = useContent();
  const { t, localePath } = useI18n();
  const svc = SERVICES[active];

  return (
    <section id="services" className="relative bg-[#FBFAF8] py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14"
        >
          <div>
            <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-emerald-600 font-semibold">
              {t("services.eyebrow")}
            </span>
            <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B0F14] leading-[1.05]">
              {t("services.title")}
            </h2>
          </div>
          <p className="max-w-md text-slate-600 text-sm sm:text-base leading-relaxed">
            {t("services.blurb")}
          </p>
        </motion.div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-3 mb-10">
          {SERVICES.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setActive(i)}
              aria-pressed={active === i}
              data-testid={`services-tab-${s.id}`}
              className={`px-5 py-3 rounded-full font-semibold text-sm transition-all ${
                active === i
                  ? "bg-[#0E3B3B] text-white"
                  : "bg-[#FBFAF8] text-slate-600 border border-slate-200 hover:border-emerald-600/50 hover:text-[#0B0F14]"
              }`}
            >
              {s.tab}
            </button>
          ))}
        </div>

        <motion.div
          key={svc.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid lg:grid-cols-2 gap-8 items-stretch"
        >
          {/* Image with clipped frame */}
          <div className={`relative rounded-3xl overflow-hidden group min-h-[320px] lg:min-h-[460px] border border-slate-200 ${svc.id === "rolloff" ? "bg-gradient-to-br from-white to-[#F0EFEA]" : ""}`}>
            <img
              loading="lazy"
              decoding="async"
              src={svc.image}
              alt={svc.title}
              className={`absolute inset-0 w-full h-full transition-transform duration-700 group-hover:scale-105 ${svc.id === "rolloff" ? "object-contain p-6" : "object-cover"}`}
            />
            <div className="absolute top-5 left-5 hazard-stripe h-2 w-20 rounded-full" />
            <div className="absolute bottom-6 left-6 right-6">
              <span className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-[#FBFAF8]/90 backdrop-blur border border-slate-200 font-mono-tech text-[11px] uppercase tracking-widest text-[#0B0F14]">
                <span className="w-2 h-2 rounded-full bg-emerald-600" />
                {svc.badge}
              </span>
            </div>
          </div>

          {/* Detail */}
          <div className="bg-[#F0EFEA] border border-slate-200 rounded-3xl p-8 sm:p-10 flex flex-col">
            <div className="font-mono-tech text-emerald-600 text-sm mb-3">{svc.capacity}</div>
            <h3 className="font-display font-bold text-2xl sm:text-3xl text-[#0B0F14] mb-4">{svc.title}</h3>
            <p className="text-slate-600 leading-relaxed text-sm sm:text-base">{svc.description}</p>

            <ul className="mt-7 grid sm:grid-cols-2 gap-3">
              {svc.points.map((p) => (
                <li key={p} className="flex items-center gap-2.5 text-sm text-[#0B0F14]">
                  <span className="grid place-items-center w-5 h-5 rounded-full bg-emerald-600/10 text-emerald-600">
                    <Check size={13} strokeWidth={3} />
                  </span>
                  {p}
                </li>
              ))}
            </ul>

            <div className="mt-auto pt-8 flex flex-wrap items-center gap-4">
              <Link
                to={localePath(svc.id === "business" ? "/business-waste-service" : "/dumpster-rentals")}
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[#0E3B3B] text-white font-bold text-sm hover:bg-[#0A2C2C] active:scale-95 transition-all"
                data-testid={`service-page-${svc.id}`}
              >
                {svc.id === "business" ? t("services.exploreBusiness") : t("services.exploreRolloff")}
                <ArrowRight size={17} className="group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <a
                href="#quote"
                className="inline-flex items-center gap-2 text-[#0B0F14] font-semibold hover:text-emerald-600 transition-colors group w-fit"
                data-testid={`service-quote-${svc.id}`}
              >
                {t("cta.requestQuote")}
                <ArrowUpRight size={18} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
